import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useMemo } from "react";
import {
  Loader2,
  Plus,
  Package,
  X,
  PlusCircle,
  Check,
  Search,
  Minus,
  Wrench,
  PackagePlus,
} from "lucide-react";
import { CreateProductModal } from "@/components/products/CreateProductModal";
import { ProductsProvider } from "@/components/products/ProductsContext";
import { productCategoryService } from "@/services/product-category.service";
import { ProductCategory } from "@/types/product";
import { useToast } from "@/hooks/use-toast";
import {
  AddItemWithoutRulesData,
  AddItemWithRulesData,
} from "@/services/opportunityService";
import { Product as ServiceProduct } from "@/services/product.service";
import { Badge } from "@/components/ui/badge";
import { useEffect } from "react";

export interface Product extends ServiceProduct {
  price?: number;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProduct: (data: AddItemWithRulesData & { id?: string }) => void;
  allProducts: Product[];
  isLoading?: boolean;
  initialData?: {
    id?: string;
    product_id: string;
    quantity: number;
    base_price: number;
    includes_installation?: boolean;
    applied_rules: { rule_id: string; value: string }[];
  } | null;
  slug: string;
  onRefreshProducts?: () => void;
}

export function AddProductModal({
  isOpen,
  onClose,
  onAddProduct,
  allProducts,
  isLoading,
  initialData,
  slug,
  onRefreshProducts,
}: AddProductModalProps) {
  const { toast } = useToast();
  const [selectedProductId, setSelectedProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [basePrice, setBasePrice] = useState<string>("0");
  const [customRules, setCustomRules] = useState<{
    id: string;
    rule_name: string;
    description: string;
    price_modifier: string;
    price_type: "fixed" | "per_unit" | "percentage";
  }[]>([]);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  useEffect(() => {
    if (isOpen && slug) {
      productCategoryService.getProductCategories({ slug, is_active: true }).then(res => {
        const mapped = (res || []).map(cat => ({
          id: cat.id,
          name: cat.name,
          description: cat.description || "",
          color: cat.color || "#3b82f6",
          createdAt: cat.created_at,
          updatedAt: cat.updated_at
        }));
        setCategories(mapped);
      }).catch(err => console.error("Error loading categories", err));
    }
  }, [isOpen, slug]);

  // Synchronize state with initialData when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setSelectedProductId(initialData.product_id);
        setQuantity(initialData.quantity.toString());
        setBasePrice(initialData.base_price.toString());
        
        // Separar reglas de catálogo de reglas personalizadas
        const catalogRuleIds: string[] = [];
        const customItems: any[] = [];
        
        initialData.applied_rules.forEach((r: any) => {
          if (r.is_custom) {
            customItems.push({
              id: r.rule_id || `custom-${Math.random()}`,
              rule_name: r.rule_name || "Servicio Adicional",
              description: r.description || "",
              price_modifier: (r.price_modifier || r.calculated_amount || "0").toString(),
              price_type: r.price_type || "fixed"
            } as any);
          } else {
            catalogRuleIds.push(r.rule_id);
          }
        });

        setSelectedRules(catalogRuleIds);
        setCustomRules(customItems);
        
        const product = allProducts.find(p => p.id === initialData.product_id);
        setSelectedProduct(product || null);
      } else {
        resetForm();
      }
    }
  }, [isOpen, initialData, allProducts]);

  // Ensure cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProductId) {
      toast({
        title: "Error",
        description: "Por favor selecciona un producto",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const appliedRules = [
        ...(selectedProduct?.rules
          ?.filter((r) => selectedRules.includes(r.id))
          .map((r) => ({
            rule_id: r.id,
            value: r.price_modifier.toString(),
          })) || []),
        ...customRules.map(cr => ({
          rule_id: cr.id,
          rule_name: cr.rule_name,
          description: cr.description,
          price_modifier: cr.price_modifier,
          price_type: cr.price_type,
          is_custom: true,
          value: cr.price_type === "per_unit" ? (parseInt(quantity) || 1).toString() : "1"
        }))
      ];

      const productData: AddItemWithRulesData & { id?: string } = {
        id: initialData?.id,
        product_id: selectedProductId,
        quantity: parseInt(quantity) || 1,
        opportunity_id: "",
        base_price: parseFloat(basePrice) || 0,
        applied_rules: appliedRules as any,
      };

      console.log("🚀 FRONTEND: Preparando datos del producto para guardar:", productData);
      onAddProduct(productData);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al agregar producto",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedProductId("");
    setQuantity("1");
    setSelectedProduct(null);
    setSelectedRules([]);
    setSearchQuery("");
    setBasePrice("0");
    setCustomRules([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleProductChange = (productId: string) => {
    setSelectedProductId(productId);
    const product = allProducts.find((p) => p.id === productId);
    setSelectedProduct(product || null);

    if (product) {
      const price = getProductPrice(product);
      setBasePrice(price.toString());
      
      if (product.rules) {
        const requiredRules = product.rules
          .filter((r) => r.is_required)
          .map((r) => r.id);
        setSelectedRules(requiredRules);
      } else {
        setSelectedRules([]);
      }
    } else {
      setBasePrice("0");
      setSelectedRules([]);
    }
  };

  const toggleRule = (ruleId: string, isRequired: boolean) => {
    if (isRequired) return;
    setSelectedRules((prev) =>
      prev.includes(ruleId)
        ? prev.filter((id) => id !== ruleId)
        : [...prev, ruleId],
    );
  };

  const addCustomService = () => {
    setCustomRules(prev => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        rule_name: "Nuevo Servicio",
        description: "",
        price_modifier: "0",
        price_type: "fixed" as const
      }
    ]);
  };

  const updateCustomService = (id: string, field: string, value: any) => {
    setCustomRules(prev => prev.map(cr => 
      cr.id === id ? { ...cr, [field]: value } : cr
    ));
  };

  const removeCustomService = (id: string) => {
    setCustomRules(prev => prev.filter(cr => cr.id !== id));
  };

  const getProductPrice = (product: Product): number => {
    if (product.price !== undefined) return product.price;
    if (typeof product.base_price === "string") {
      return parseFloat(product.base_price) || 0;
    }
    return product.base_price || 0;
  };

  const calculateTotalPrice = () => {
    if (!selectedProduct) return 0;
    
    const numericBasePrice = parseFloat(basePrice) || 0;
    const numericQuantity = parseInt(quantity) || 0;
    let total = numericBasePrice * numericQuantity;
    
    // Sum catalog rules
    selectedRules.forEach(ruleId => {
      const rule = selectedProduct.rules?.find(r => r.id === ruleId);
      if (rule) {
        if (rule.price_type === "fixed") {
          total += parseFloat(rule.price_modifier.toString());
        } else if ((rule.price_type as string) === "per_unit") {
          total += parseFloat(rule.price_modifier.toString()) * numericQuantity;
        } else if (rule.price_type === "percentage") {
          total += (numericBasePrice * numericQuantity) * (parseFloat(rule.price_modifier.toString()) / 100);
        }
      }
    });
    
    // Sum custom rules
    customRules.forEach(cr => {
      const val = parseFloat(cr.price_modifier) || 0;
      if (cr.price_type === "percentage") {
        total += (numericBasePrice * numericQuantity) * (val / 100);
      } else if (cr.price_type === "per_unit") {
        total += val * numericQuantity;
      } else {
        // fixed: applies once regardless of quantity
        total += val;
      }
    });
    
    return total;
  };

  const modalTitle = initialData ? "Editar Producto" : "Agregar Producto al Catálogo";
  const submitButtonText = initialData ? "Guardar Cambios" : "Agregar Producto";
  const totalPrice = calculateTotalPrice();

  const rules = selectedProduct?.rules || [];
  
  const filteredProducts = useMemo(
    () =>
      allProducts.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.sku.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [allProducts, searchQuery],
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-green-600" />
            {modalTitle}
          </DialogTitle>
          <DialogDescription>
            {initialData 
              ? "Modifica la cantidad, reglas o precio base de este producto."
              : "Selecciona un producto del catálogo para añadirlo a esta oportunidad."
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              {!selectedProduct ? (
                <div className="space-y-2">
                  <Label htmlFor="product-search" className="text-sm">
                    Buscar Producto *
                  </Label>
                   <div className="relative flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="product-search"
                        placeholder="Buscar por nombre o SKU..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={isLoading}
                        className="pl-10 h-10"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 gap-2 border-green-200 text-green-700 hover:bg-green-50"
                      onClick={() => setShowCreateProductModal(true)}
                    >
                      <PackagePlus className="h-4 w-4" />
                      Nuevo Producto
                    </Button>
                  </div>
                  <div className="border rounded-md max-h-80 overflow-y-auto">
                    {isLoading ? (
                      <div className="flex items-center justify-center p-4 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span>Cargando productos...</span>
                      </div>
                    ) : filteredProducts.length > 0 ? (
                      filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          onClick={() => handleProductChange(product.id)}
                          className={`p-3 cursor-pointer border-b last:border-b-0 flex justify-between items-start ${
                            selectedProductId === product.id
                              ? "bg-blue-50"
                              : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {product.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              SKU: {product.sku}
                            </p>
                          </div>
                          <div className="text-right ml-4 flex-shrink-0">
                            <p className="font-semibold text-green-600 text-sm">
                              ${formatCurrency(getProductPrice(product))}
                            </p>
                            {product.category?.name && (
                              <Badge
                                variant="outline"
                                className="text-xs h-5 mt-1"
                              >
                                {product.category.name}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-gray-500">
                        No se encontraron productos.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-sm">
                        Producto Seleccionado
                      </h4>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() => {
                          setSelectedProductId("");
                          setSelectedProduct(null);
                          setSelectedRules([]);
                          setSearchQuery("");
                        }}
                      >
                        Cambiar
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-xs text-slate-600">
                          Precio unitario:
                        </span>
                        <p className="text-sm font-semibold text-green-600 mt-0.5">
                          ${formatCurrency(getProductPrice(selectedProduct))}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-600">SKU:</span>
                        <p className="text-sm font-medium mt-0.5">
                          {selectedProduct.sku}
                        </p>
                      </div>
                    </div>
                  </div>

                  {rules.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-blue-900">
                        Ajustes de Catálogo
                      </Label>
                      <div className="grid grid-cols-1 gap-2">
                        {rules.map((rule) => (
                          <div
                            key={rule.id}
                            className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                              selectedRules.includes(rule.id)
                                ? "bg-blue-50 border-blue-200"
                                : "border-slate-200 hover:bg-slate-50"
                            } ${
                              rule.is_required
                                ? "border-amber-200 bg-amber-50"
                                : ""
                            }`}
                            onClick={() => toggleRule(rule.id, rule.is_required)}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <div
                                className={`h-4 w-4 rounded border flex items-center justify-center ${
                                  selectedRules.includes(rule.id)
                                    ? "bg-blue-600 border-blue-600"
                                    : "border-slate-300 bg-white"
                                } ${
                                  rule.is_required
                                    ? "border-amber-400 bg-amber-100"
                                    : ""
                                }`}
                              >
                                {selectedRules.includes(rule.id) && (
                                  <Check className="h-3 w-3 text-white" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-medium text-xs truncate">
                                    {rule.rule_name}
                                  </span>
                                  {rule.is_required && (
                                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px] h-4">
                                      Obligatorio
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className={`text-xs font-semibold ml-2 whitespace-nowrap ${
                              parseFloat(rule.price_modifier.toString()) >= 0 ? "text-green-600" : "text-red-600"
                            }`}>
                              {rule.price_type === "percentage" ? `${rule.price_modifier}%` : `$${rule.price_modifier}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 pt-3 border-t">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-bold text-blue-900 flex items-center">
                        <Wrench className="w-4 h-4 mr-1.5" /> Servicios y Extras Personalizados
                      </Label>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-[10px] border-blue-200 text-blue-700"
                        onClick={addCustomService}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Agregar Servicio
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      {customRules.map((cr) => (
                        <div key={cr.id} className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2 relative group">
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-red-100 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => removeCustomService(cr.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-[10px] text-blue-700">Nombre del Servicio</Label>
                              <Select 
                                value={cr.rule_name} 
                                onValueChange={(val) => updateCustomService(cr.id, "rule_name", val)}
                              >
                                <SelectTrigger className="h-8 text-xs bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Instalación">Instalación</SelectItem>
                                  <SelectItem value="Mantenimiento">Mantenimiento</SelectItem>
                                  <SelectItem value="Extras">Extras</SelectItem>
                                  <SelectItem value="Diseño">Diseño</SelectItem>
                                  <SelectItem value="Otros">Otros</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] text-blue-700">Tipo de Ajuste</Label>
                              <Select 
                                value={cr.price_type} 
                                onValueChange={(val: any) => updateCustomService(cr.id, "price_type", val)}
                              >
                                <SelectTrigger className="h-8 text-xs bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="fixed">Fijo Total (una sola vez)</SelectItem>
                                  <SelectItem value="per_unit">Por Unidad (× cantidad)</SelectItem>
                                  <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] text-blue-700">
                                Valor del Ajuste
                                {cr.price_type === "fixed" && <span className="ml-1 text-slate-400 font-normal">(total único)</span>}
                                {cr.price_type === "per_unit" && <span className="ml-1 text-slate-400 font-normal">(× {parseInt(quantity)||1} uds)</span>}
                              </Label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                                  <span className="text-[10px] text-slate-500 font-bold">
                                    {cr.price_type === "percentage" ? "%" : "$"}
                                  </span>
                                </div>
                                <Input 
                                  type="text" 
                                  className="h-8 text-xs bg-white font-bold text-green-700 pl-5" 
                                  value={cr.price_modifier}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9.]/g, "");
                                    updateCustomService(cr.id, "price_modifier", val);
                                  }}
                                />
                              </div>
                              {cr.price_type === "per_unit" && (
                                <p className="text-[10px] text-amber-600">
                                  = ${(parseFloat(cr.price_modifier)||0) * (parseInt(quantity)||1)} total ({cr.price_modifier} × {parseInt(quantity)||1})
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px] text-blue-700">Descripción / Detalles</Label>
                            <Input 
                              placeholder="Ej: Instalación en altura, materiales incluidos..." 
                              className="h-8 text-xs bg-white"
                              value={cr.description}
                              onChange={(e) => updateCustomService(cr.id, "description", e.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                      {customRules.length === 0 && (
                        <div className="text-center py-4 border border-dashed rounded-lg text-slate-400 text-xs">
                          No has agregado servicios adicionales.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="quantity" className="text-sm">
                  Cantidad *
                </Label>
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const current = parseInt(quantity) || 1;
                      setQuantity(Math.max(1, current - 1).toString());
                    }}
                    disabled={parseInt(quantity) <= 1}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <Input
                    id="quantity"
                    type="text"
                    min="1"
                    value={quantity}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setQuantity(val);
                    }}
                    className="text-center h-8"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const current = parseInt(quantity) || 0;
                      setQuantity((current + 1).toString());
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {selectedProduct && (
                <div className="space-y-4 pt-2 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="basePrice" className="text-sm font-medium text-blue-900">
                      Modificar Precio Base (Opcional)
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-slate-500 sm:text-sm">$</span>
                      </div>
                      <Input
                        id="basePrice"
                        type="text"
                        value={basePrice}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, "");
                          setBasePrice(val);
                        }}
                        className="pl-7 font-bold text-blue-900 border-blue-200 focus-visible:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-md p-3 border border-blue-200">
                    <h4 className="font-medium text-sm mb-2 text-blue-900">
                      Resumen Final
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-blue-700">Subtotal Productos:</span>
                        <span className="font-semibold">${formatCurrency((parseFloat(basePrice) || 0) * (parseInt(quantity) || 0))}</span>
                      </div>
                      {customRules.length > 0 && customRules.map(cr => {
                        const val = parseFloat(cr.price_modifier) || 0;
                        const qty = parseInt(quantity) || 1;
                        let crTotal = 0;
                        if (cr.price_type === "percentage") crTotal = (parseFloat(basePrice)||0) * qty * (val/100);
                        else if (cr.price_type === "per_unit") crTotal = val * qty;
                        else crTotal = val;
                        return (
                          <div key={cr.id} className="flex justify-between items-center text-xs">
                            <span className="text-blue-700 truncate max-w-[120px]">{cr.rule_name}:</span>
                            <span className="font-semibold text-green-700 whitespace-nowrap">
                              +${formatCurrency(crTotal)}
                              {cr.price_type === "per_unit" && <span className="text-[9px] text-slate-400 ml-1">(×{qty})</span>}
                              {cr.price_type === "fixed" && <span className="text-[9px] text-slate-400 ml-1">(único)</span>}
                            </span>
                          </div>
                        );
                      })}
                      <div className="pt-2 border-t border-blue-300 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-bold text-blue-900">Total:</span>
                          <span className="text-lg font-bold text-blue-700">
                            ${formatCurrency(totalPrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="pt-3 border-t">
            <div className="flex gap-2 justify-end w-full">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" disabled={!selectedProductId || isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-2 h-4 w-4" />}
                {submitButtonText}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
      
      <ProductsProvider slug={slug}>
         <CreateProductModal
            isOpen={showCreateProductModal}
            onClose={() => setShowCreateProductModal(false)}
            categories={categories}
            onSave={async (newProduct) => {
               if (onRefreshProducts) {
                  onRefreshProducts();
               }
               // Seleccionar automáticamente el producto recién creado
               if (newProduct && newProduct.id) {
                  handleProductChange(newProduct.id);
               }
               setShowCreateProductModal(false);
               toast({
                  title: "Producto creado",
                  description: `El producto "${newProduct.name}" ha sido creado y seleccionado.`,
               });
            }}
         />
      </ProductsProvider>
    </Dialog>
  );
}
